import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ElementRef,
    ViewEncapsulation,
    effect,
    inject,
    input,
    model,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

/**
 * Native port of the AngularJS `ftImageLibrary` component family for the homepage messaging
 * "Fixed quote profile image" modal (distinct from the newer ft2 `ft-image-library-editor`; this
 * reproduces the v1 `.imageLibrary-*` markup for parity, with `encapsulation: None` for global LESS).
 * Tabs: Library (list → preview, emits `imageSelected`), Upload (global `Dropzone`), and Upload-from-URL
 * (Angular `HttpClient`). Uploads push the new image into the list and select it.
 */
@Component({
    selector: 'ft-image-library-ng',
    imports: [FormsModule, TranslateModule],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './ft-image-library.component.html'
})
export class FtImageLibraryComponent implements AfterViewInit {
    /** The raw `asset_images` array (each `{url,name,…}`) — bound directly (v1 binds `image.url`). */
    readonly images = model<any[]>([]);
    readonly propertyId = input('');
    readonly imageType = input<any>(1);
    /** v1 `view` attr (default 'list'). */
    readonly view = model<'list' | 'preview' | 'upload' | 'upload-from-url'>('list');
    /** v1 `on-image-selected(image)` — fired when a list thumbnail is picked / an upload completes. */
    readonly imageSelected = output<any>();

    protected readonly selectedImage = signal<any>(null);
    protected readonly imageUrl = signal('');
    protected readonly uploadFailed = signal(false);
    /** v1 imageLibrary.js `isProcessingUpload` — drives the panel ftLoadingAnimation overlay
     *  ("Processing image…" + spinner, pointer-events:none) during BOTH upload paths. */
    protected readonly isProcessingUpload = signal(false);
    private dropzoneWired = false;

    private readonly dropzoneEl = viewChild<ElementRef<HTMLElement>>('dropzone');

    private readonly http = inject(HttpClient);

    constructor() {
        effect(() => {
            if (!this.view()) this.view.set('list');
        });
    }

    ngAfterViewInit(): void {
        this.wireDropzone();
    }

    protected showPreview(image: any): void {
        this.selectImage(image);
    }

    protected useDifferentImage(): void {
        this.view.set('list');
        this.selectedImage.set(null);
    }

    /** url(...) for the inline `background-image` (v1 imageList sets it unconditionally). */
    protected bgUrl(url: string): string {
        return url ? `url(${url})` : '';
    }

    /** v1 ctrl.selectImage + the `$watch('image')` → on-image-selected. */
    private selectImage(image: any): void {
        this.view.set('preview');
        this.selectedImage.set(image);
        this.imageSelected.emit(image);
    }

    /** v1 ctrl.addImage → images.push (immutable here so OnPush-safe). */
    private addAndSelect(image: any): void {
        if (!image) return;
        this.images.set([...(this.images() || []), image]);
        this.selectImage(image);
    }

    /** v1 ftImageUploadFromUrl.upload → Image.uploadPropertyImageFromUrl. */
    protected async uploadFromUrl(): Promise<void> {
        const raw = (this.imageUrl() || '').trim();
        if (!raw) return;
        const hasScheme = /^http(s)?:\/\//gi.test(raw);
        const url = hasScheme ? raw : 'http://' + raw;
        this.imageUrl.set('');
        // v1 imageUploadFromUrl.js: onImageUploading() fires before the request → overlay on.
        // (v1 had no error handler, leaving the overlay stuck on failure — we clear it instead.)
        this.isProcessingUpload.set(true);
        try {
            const endpoint = `/api/${this.propertyId()}/properties/${this.propertyId()}/imageFromUrl?type=${this.imageType()}&url=${encodeURIComponent(url)}`;
            const image = await firstValueFrom(this.http.post<any>(endpoint, null));
            this.addAndSelect(image);
        } finally {
            this.isProcessingUpload.set(false);
        }
    }

    /** Verbatim v1 imageUpload.js Dropzone wiring (file upload). Bridge-free (global Dropzone). */
    private wireDropzone(): void {
        const dropzoneEl = this.dropzoneEl();
        if (this.dropzoneWired || !dropzoneEl) return;
        const DropzoneCtor = (window as any).Dropzone;
        if (!DropzoneCtor) return;
        this.dropzoneWired = true;
        const uploadUrl = `/api/${this.propertyId()}/properties/${this.propertyId()}/image?type=${this.imageType()}`;
        const self = this;
        // eslint-disable-next-line no-new
        new DropzoneCtor(dropzoneEl.nativeElement, {
            url: uploadUrl,
            headers: { 'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') },
            maxFiles: 1,
            previewsContainer: false,
            init: function (this: any) {
                // v1 imageUpload.js: addedfile also fires onImageUploading() → overlay during
                // file uploads too; success/error fire onImageUploaded() → overlay off.
                this.on('addedfile', () => {
                    self.uploadFailed.set(false);
                    self.isProcessingUpload.set(true);
                });
                this.on('success', (_file: any, response: any) => {
                    self.addAndSelect(response);
                    self.isProcessingUpload.set(false);
                });
                this.on('error', () => {
                    self.uploadFailed.set(true);
                    self.isProcessingUpload.set(false);
                });
                this.on('maxfilesexceeded', function (this: any, file: any) {
                    this.removeAllFiles();
                    this.addFile(file);
                });
            },
        });
    }
}

/** Verbatim v1 imageUpload.js getCookie (read the XSRF-TOKEN for the Dropzone POST header). */
function getCookie(key: string): string | undefined {
    const cookieString = typeof document !== 'undefined' ? document.cookie : '';
    const cookies = cookieString ? cookieString.split('; ') : [];
    for (const cookie of cookies) {
        const index = cookie.indexOf('=');
        if (index > 0 && decodeURIComponent(cookie.substring(0, index)) === key) {
            return decodeURIComponent(cookie.substring(index + 1));
        }
    }
    return undefined;
}
